import { SpeechSDK } from '@/hooks/useAzureSynthesizer';
import { isV3Model } from '@/pages/AdvancedEditor/utils/breakConversion';
import { isElevenLabsEnglishVoice } from '@/services/d-id/talks';
import { logger } from '@/services/logger';
import { TtsData } from '@/services/tts/tts-data/types';
import { AudioError, convertErrorToAudioError, DEFAULT_VOICE_CONFIG } from '@/state/presenters';
import { escapeSsmlText } from '@d-id/ssml';
import { IVoice, Providers, VoiceConfig } from '@d-id/types';
import { concatArrayBuffers, decode, endStream, getReader } from './streamUtils';

/**
 * Get the tts data using the stream provider
 * @param setInlineTtsError
 * @param token
 * @param streamLambdaUrl
 * @param text
 * @param voice
 * @param voiceConfig
 * @returns tts data
 */
interface Params {
    setInlineTtsError: (ttsError: AudioError) => void;
    token?: string;
    streamLambdaUrl?: string;
    text: string;
    voice: IVoice | null;
    voiceConfig?: VoiceConfig;
}

export const MAX_TEXT_LENGTH_STREAM = 1000;

export const getStreamTtsData = ({
    setInlineTtsError,
    token,
    streamLambdaUrl,
    text,
    voice,
    voiceConfig = DEFAULT_VOICE_CONFIG,
}: Params): Promise<TtsData> => {
    return new Promise(async (resolve, reject) => {
        if (!voice || !text || !streamLambdaUrl) {
            logger.error('Insufficient params');
            return reject('Insufficient params');
        }

        if (voice.provider !== Providers.Elevenlabs) {
            logger.error('Incorrect stream provider');
            return reject('Incorrect stream provider');
        }

        if (!token || !SpeechSDK) {
            logger.error('No token or SpeechSDK');
            return reject('No token or SpeechSDK');
        }

        const controller = new AbortController();
        const url = streamLambdaUrl + voice.provider;
        const modelId =
            voice.provider === Providers.Elevenlabs
                ? voice.config?.modelId || (!isElevenLabsEnglishVoice(voice.locale) ? 'eleven_multilingual_v2' : undefined)
                : undefined;
        const speed = voiceConfig.rate;
        const stability = voice.provider === Providers.Elevenlabs ? voiceConfig?.stability : undefined;
        const similarityBoost = voice.provider === Providers.Elevenlabs ? voiceConfig?.similarity_boost : undefined;

        const allowedText = text.slice(0, MAX_TEXT_LENGTH_STREAM);
        const isV3 = isV3Model(modelId);
        const ssmlText = escapeSsmlText(allowedText, true, isV3);
        const reader = await getReader(
            url,
            voice.id,
            ssmlText,
            token,
            controller.signal,
            error => {
                setInlineTtsError(convertErrorToAudioError(error));
                reject(error);
            },
            modelId,
            speed,
            undefined,
            stability,
            similarityBoost
        );

        if (reader) {
            const audio = document.createElement('audio');
            const mediaSource = new window.MediaSource();

            audio.src = window.URL.createObjectURL(mediaSource);

            audio.onended = () => {
                audio.remove();
            };

            audio.onerror = e => {
                setInlineTtsError(convertErrorToAudioError(e));
                reject(e);
            };

            mediaSource.onsourceopen = async _ => {
                const buffers: ArrayBufferLike[] = [];
                const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
                let read: Awaited<ReturnType<typeof reader.read>> | null = null;
                let lastRead: Awaited<ReturnType<typeof reader.read>> | null = null;

                function appendBuffer() {
                    if (sourceBuffer.updating === false && mediaSource.readyState === 'open') {
                        const currentBuffer = buffers.pop();

                        if (currentBuffer) {
                            sourceBuffer.appendBuffer(currentBuffer as BufferSource);
                        } else if (read?.done) {
                            endStream(mediaSource, lastRead, (e: any) => {
                                setInlineTtsError(convertErrorToAudioError(e));
                                reject(e);
                            });
                        }
                    }
                }

                audio.volume = 0;

                audio.play();

                sourceBuffer.addEventListener('updateend', appendBuffer);

                sourceBuffer.addEventListener('error', e => {
                    if (lastRead) {
                        const decoded = decode(lastRead?.value);

                        if (decoded?.kind) {
                            setInlineTtsError(convertErrorToAudioError(decoded.kind));
                        } else {
                            logger.error(e);
                        }

                        reject(e);
                    }
                });

                try {
                    const cacheTtsBuffers = [];
                    do {
                        lastRead = read;
                        read = await reader.read();

                        if (read.value?.buffer) {
                            buffers.push(read.value.buffer);
                            cacheTtsBuffers.push(read.value.buffer);

                            appendBuffer();
                        }
                    } while (read && !read.done);
                    const mergedArrayBuffer = concatArrayBuffers(cacheTtsBuffers);
                    const clonedArrayBuffer = mergedArrayBuffer.slice(0);

                    // Get the duration of the audio
                    const audioContext = new AudioContext();

                    try {
                        const audioBuffer = await audioContext.decodeAudioData(clonedArrayBuffer);
                        const duration = audioBuffer.duration;
                        resolve({ data: mergedArrayBuffer, duration });
                    } catch (error) {
                        logger.error('Error decoding audio data:', error);
                        reject({ data: mergedArrayBuffer, duration: 0, error });
                    } finally {
                        audioContext.close();
                    }
                } catch (e) {
                    setInlineTtsError('unknown');
                    reject(e);
                } finally {
                    if (buffers.length === 0 && sourceBuffer.updating === false && mediaSource.readyState === 'open') {
                        endStream(mediaSource, lastRead, (e: any) => {
                            setInlineTtsError(convertErrorToAudioError(e));
                        });
                    }
                }
            };
        }
    });
};
